import ApiService from "@/core/services/ApiService";
import { AxiosResponse } from "axios";

/**
 * Interface para o retorno do status da certificação
 */
export interface CertificationStatusResponse {
  data: {
    current_step: number;
    current_step_started_at?: string; // Momento que o usuário entrou no step atual
    deadline?: string; // Deadline principal calculado pelo backend baseado no step atual
    deadline_send_report?: string; // Deadline para envio de relatório (step de report)
    created_at?: string; // Data de início da certificação
    retry_available_at?: string | null; // Data em que o usuário reprovado pode tentar novamente
    can_retry?: boolean; // Reprovado e cooldown já encerrado
    certification_data?: {
      modeling_deadline?: string; // Deadline de modelagem (estratégia CASP)
      [key: string]: any;
    };
    [key: string]: any;
  };
}

/**
 * Interface para o retorno da config da certificação
 */
export interface CertificationConfigResponse {
  data: {
    id: number;
    name: string;
    is_active: boolean;
    welcome_message: string | null;
    approval_message: string | null;
    retry_timeout_months: number;
    video_url: string | null;
    strategy_type: string;
    has_machines: boolean;
    has_report: boolean;
    has_custom_steps: boolean;
    steps: Array<{
      id?: number;
      order: number;
      type: string;
      component: string;
      deadline_days?: number | null;
      required: boolean;
      title: string;
      message: string | null;
      description: string;
      attachments?: Array<{
        id: number;
        name: string;
        mime_type: string | null;
        file_size: number | null;
      }>;
    }>;
    settings: {
      minimum_grade: number;
      allow_retries: boolean;
      retry_cooldown_days: number;
      minimum_machines_required: number;
    };
  };
}

/**
 * Serviço para gerenciar chamadas à API de certificação
 */
class CertificationService {
  /**
   * Busca o status atual da certificação do usuário
   * @param certificationId - ID da certificação
   * @returns Promise com a resposta do status
   */
  public static async getStatus(certificationId: string | number): Promise<AxiosResponse<CertificationStatusResponse>> {
    return ApiService.get(`/certification/status/${certificationId}`);
  }

  /**
   * Busca a configuração da certificação
   * @param certificationId - ID da certificação
   * @returns Promise com a resposta da config
   */
  public static async getConfig(certificationId: string | number): Promise<AxiosResponse<CertificationConfigResponse>> {
    return ApiService.get(`/certification/${certificationId}/config`);
  }
}

export default CertificationService;
